import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Phone } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { EASE } from "@/components/site/Reveal";
import { useLang } from "@/i18n";
import { CONTACT } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ label, value, testId }) => (
  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-6 py-4 border-b border-white/10">
    <dt className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{label}</dt>
    <dd className="text-white sm:text-right" data-testid={testId}>{value}</dd>
  </div>
);

const Seal = () => (
  <motion.svg viewBox="0 0 120 120" className="h-24 w-24" initial="hidden" animate="show">
    <motion.circle cx="60" cy="60" r="54" fill="none" stroke="#DC2626" strokeWidth="3" variants={{ hidden: { pathLength: 0 }, show: { pathLength: 1 } }} transition={{ duration: 1.2, ease: EASE }} />
    <motion.path d="M38 62 54 77 84 45" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="square" variants={{ hidden: { pathLength: 0 }, show: { pathLength: 1 } }} transition={{ duration: 0.6, delay: 1, ease: EASE }} />
  </motion.svg>
);

export default function Confirmation() {
  const { ref } = useParams();
  const { t, lang } = useLang();
  const c = t.confirm;
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/bookings/${encodeURIComponent(ref)}`)
      .then((res) => { if (alive) { setData(res.data ?? null); setState(res.data ? "ok" : "missing"); } })
      .catch(() => { if (alive) setState("missing"); });
    return () => { alive = false; };
  }, [ref]);

  const date = data?.date ? new Date(`${data.date}T12:00:00`).toLocaleDateString(lang === "ro" ? "ro-RO" : "en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";
  const services = Array.isArray(data?.services) ? data.services.map((s) => t.services.items[s]?.title ?? s).join(", ") : "";

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="confirmation-page">
      <Nav />
      <main className="relative pt-36 pb-28 overflow-hidden">
        <div className="absolute inset-0 tac-grid" />
        <div className="absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-red-800/25 blur-[140px]" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
          {state === "loading" && <p className="font-mono text-sm text-zinc-400" data-testid="confirmation-loading">{c.loading}</p>}
          {state === "missing" && <p className="text-zinc-300" data-testid="confirmation-not-found">{c.notFound}</p>}
          {state === "ok" && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
              <Seal />
              <p className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-red-500">{c.eyebrow}</p>
              <h1 className="mt-4 font-display font-black uppercase text-5xl sm:text-6xl leading-[0.95]" data-testid="confirmation-title">{c.title.replace("{name}", data.first_name)}</h1>
              <p className="mt-6 text-zinc-300 leading-relaxed">{c.sub}</p>
              <div className="mt-10 border border-white/10 bg-black/60 backdrop-blur-xl p-6 sm:p-8">
                <div className="flex items-baseline justify-between gap-4 pb-4 border-b border-red-700/50">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{c.ref}</span>
                  <span className="font-display font-extrabold text-3xl text-red-500 tracking-wider" data-testid="confirmation-reference">{data.reference}</span>
                </div>
                <dl>
                  <Row label={c.systems} value={services} testId="confirmation-services" />
                  <Row label={c.property} value={`${t.booking.property[data.property_type]?.[0] ?? ""} · ${t.booking.areas[data.area] ?? ""}`} testId="confirmation-property" />
                  <Row label={c.when} value={`${date} · ${data.slot}`} testId="confirmation-when" />
                  <Row label={c.city} value={data.city} testId="confirmation-city" />
                  <Row label={c.status} value={<span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-amber-400 blink" />{c.pending}</span>} testId="confirmation-status" />
                </dl>
              </div>
            </motion.div>
          )}
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to="/" data-testid="confirmation-home-link" className="clip-btn inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 transition-colors px-6 py-4 font-mono text-xs uppercase tracking-[0.18em]"><ArrowLeft className="h-4 w-4" />{c.home}</Link>
            <a href={CONTACT.phoneHref} data-testid="confirmation-phone-link" className="clip-btn inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/60 transition-colors px-6 py-4 font-mono text-xs uppercase tracking-[0.18em]"><Phone className="h-4 w-4" />{CONTACT.phone}</a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
